<template>
  <div class="message-block">
    <div class="header-content-and-controls mb10">
      <slot name="header-content" />
      <div class="message-controls">
        <unstyled-button v-if="canBeEdited && !editing" :title="i18n.t('member.forums.edit_message')"
                         class="edit-message-button" @click="editMessage">
          <i class="icon-pencil" />
        </unstyled-button>
        <span v-if="canFlag && flagsCount > 0" :class="{flagged: isFlaggedByUser}">
          {{ flagsCount }}
        </span>
        <unstyled-button v-if="canFlag" class="flag-message-button" :class="{flagged: isFlaggedByUser}"
                         :title="i18n.t('member.forums.flag_message')"
                         :disabled="isFlaggedByUser" @click="openFlagModal">
          <i class="icon-flag" />
        </unstyled-button>
        <unstyled-button v-if="canDelete" class="delete-message-button" :title="i18n.t('member.forums.delete_message')"
                         @click="deleteMessage">
          <icon-remove />
        </unstyled-button>
      </div>
    </div>

    <form v-if="editing" class="d-grid gap-2">
      <text-input :value="editedText" :multiline="true" :validation-errors="validationErrors.content"
                  additional-input-classes="textarea-md" input-name="content" @changed="(v) => editedText = v" />
      <div class="button-row d-flex justify-content-between">
        <light-button :label="i18n.t('administrative.cancel')" class="btn-sm" @click="cancelEditMessage" />
        <submit-button :form-valid="formIsValid" :submit-in-progress="updateInProgress"
                       :label="i18n.t('administrative.update')" :submitting-label="i18n.t('buttons.updating')"
                       class="btn-sm" @click="updateMessage" />
      </div>
    </form>
    <message-content v-else class="message-content" :content="content" />

    <message-flag-modal ref="modal" :modal-id="`flag-modal-message-${id}`"
                        :message-id="id" :thread-id="threadId" :flag-action-name="flagActionName" />
  </div>
</template>

<script>
import IconRemove from '../components/icons/icon-remove'
import LightButton from '@/components/buttons/light-button'
import MessageContent from './message-content'
import MessageFlagModal from './message-flag-modal'
import { mapState } from 'vuex'
import SubmitButton from '@/components/buttons/submit-button'
import TextInput from '@/components/inputs/text-input'
import UnstyledButton from '@/components/buttons/unstyled-button'

export default {
  components: { IconRemove, LightButton, MessageContent, MessageFlagModal, SubmitButton, TextInput, UnstyledButton },

  props: {
    canBeEdited: {
      type: Boolean,
      required: true
    },
    canSubmitUpdate: {
      type: Boolean,
      required: false,
      default: true
    },
    content: {
      type: String,
      required: true
    },
    editing: {
      type: Boolean,
      required: true
    },
    id: {
      type: Number,
      required: true
    },
    isFlaggedByUser: {
      type: Boolean,
      required: true
    },
    flagsCount: {
      type: Number,
      required: false,
      default: 0
    },
    flagActionName: {
      type: String,
      required: true
    },
    threadId: {
      type: Number,
      required: true
    },
    validationErrors: {
      type: Object,
      required: true
    },
    updateInProgress: {
      type: Boolean,
      required: true
    }
  },

  emits: ['cancel-edit', 'delete', 'edit', 'update-message'],

  data() {
    return {
      editedText: this.content
    }
  },

  computed: {
    ...mapState(['canDelete', 'canFlag', 'canPost']),

    formIsValid() {
      return this.canSubmitUpdate && !!this.editedText
    }
  },

  methods: {
    cancelEditMessage() {
      this.editedText = this.content
      this.$emit('cancel-edit')
    },

    deleteMessage() {
      this.$emit('delete', {threadId: this.threadId, messageId: this.id})
    },

    editMessage() {
      this.$emit('edit')
    },

    openFlagModal() {
      this.$refs.modal.open()
    },

    updateMessage() {
      this.$emit('update-message', this.editedText)
    }
  }
}
</script>

<style scoped lang="scss">
  .message-block {
    flex: 1 1 80%;
    width: 80%;
  }

  .flag-message-button {
    font-size: 14px;
  }

  .flagged {
    color: #ff0000;
  }

  .header-content-and-controls {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
  }
</style>
